// src/pages/admin/components/GroupManagement/GroupManagement.js
import React, { useState, useEffect } from 'react';
import { fetchUsers } from '../../services/userService';
import { fetchGroups, saveGroup, deleteGroup } from '../../services/groupService';
import GroupList from './GroupList';
import GroupDetails from './GroupDetails';
import './GroupAssignments.css';

const GroupManagement = () => {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [unassignedMentors, setUnassignedMentors] = useState([]);
  const [unassignedLearners, setUnassignedLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedMentorIds, setSelectedMentorIds] = useState([]);
  const [selectedLearnerIds, setSelectedLearnerIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await fetchUsers();
      setUsers(userData.allUsers);
      setUnassignedMentors(userData.unassignedMentors);
      setUnassignedLearners(userData.unassignedLearners);
      setGroups(await fetchGroups(userData.allUsers));
    } catch (error) {
      console.error("Error loading data:", error);
      setError("Failed to load groups and users: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    group.mentors?.some(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setNewGroupName(group.name);
    setSelectedMentorIds(group.mentorIds || []);
    setSelectedLearnerIds(group.learnerIds || []);
    setError(null);
    setSuccess(null);
  };

  const handleCreateNewGroup = () => {
    setSelectedGroup({ id: 'new', name: 'New Group', mentorIds: [], learnerIds: [], mentors: [], learners: [] });
    setNewGroupName('New Group');
    setSelectedMentorIds([]);
    setSelectedLearnerIds([]);
    setError(null);
    setSuccess(null);
  };

  const handleSaveGroup = async () => {
    if (!newGroupName.trim()) { setError("Group name cannot be empty"); return; }
    try {
      const isNewGroup = !selectedGroup || selectedGroup.id === 'new' || !selectedGroup.id;
      setLoading(true); setError(null); setSuccess(null);

      const savedGroupId = await saveGroup(
        { name: newGroupName, mentorIds: selectedMentorIds, learnerIds: selectedLearnerIds },
        isNewGroup ? null : selectedGroup.id,
        isNewGroup ? [] : selectedGroup.mentorIds || [],
        isNewGroup ? [] : selectedGroup.learnerIds || []
      );

      const userData = await fetchUsers();
      setUsers(userData.allUsers);
      setUnassignedMentors(userData.unassignedMentors);
      setUnassignedLearners(userData.unassignedLearners);
      const groupsData = await fetchGroups(userData.allUsers);
      setGroups(groupsData);

      const saved = groupsData.find(g => g.id === savedGroupId);
      if (saved) {
        setSelectedGroup(saved);
        setSelectedMentorIds(saved.mentorIds || []);
        setSelectedLearnerIds(saved.learnerIds || []);
        setSuccess(`Group "${saved.name}" ${isNewGroup ? 'created' : 'updated'} successfully`);
      }
    } catch (error) {
      console.error("Error saving group:", error);
      setError("Error saving group: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;
    if (!window.confirm(`Delete "${selectedGroup.name}"?`)) return;
    try {
      setLoading(true); setError(null); setSuccess(null);
      await deleteGroup(selectedGroup.id, selectedGroup.mentorIds || [], selectedGroup.learnerIds || []);
      const userData = await fetchUsers();
      setUsers(userData.allUsers);
      setUnassignedMentors(userData.unassignedMentors);
      setUnassignedLearners(userData.unassignedLearners);
      setGroups(await fetchGroups(userData.allUsers));
      setSelectedGroup(null); setNewGroupName(''); setSelectedMentorIds([]); setSelectedLearnerIds([]);
      setSuccess(`Group deleted successfully`);
    } catch (error) {
      setError(`Failed to delete group: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !groups.length) return <div className="loading">Loading groups and users...</div>;

  return (
    <div className="groups-management">
      <h2>Group Management</h2>
      <p>Create and manage learning groups, assign mentors and learners.</p>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      <div className="search-container">
        <input type="text" placeholder="Search groups or mentors..." value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
      </div>
      <div className="groups-container">
        <GroupList groups={filteredGroups} selectedGroup={selectedGroup}
          unassignedMentors={unassignedMentors} unassignedLearners={unassignedLearners}
          onGroupSelect={handleGroupSelect} onCreateNew={handleCreateNewGroup} />
        <GroupDetails selectedGroup={selectedGroup} newGroupName={newGroupName}
          setNewGroupName={setNewGroupName} selectedMentorIds={selectedMentorIds}
          setSelectedMentorIds={setSelectedMentorIds}
          allMentors={users.filter(u => u.role === 'mentor')}
          onSave={handleSaveGroup} onDelete={handleDeleteGroup} />
      </div>
    </div>
  );
};

export default GroupManagement;